import { COOKIES, DEFAULT_STATE, TYPES } from '@config/constants'
import { isUndefined } from '@core/helpers'
import { setCookie } from '@core/helpers/cookies'
import { saveCookiesSettings } from '@http/endpoints'
import retry from '@http/retry'
import { COUNTRIES_WITHOUT_MODAL } from '@legal/constants'
import { trackGDPR } from '@tracking/events'

import { createConfig } from './config'

export default {
  namespaced: true,
  state() {
    return {
      ...DEFAULT_STATE,
      isOpen: false,
    }
  },
  getters: {
    all: ({ isOpen, ...state }) => state,
    isOpen: (state) => state.isOpen,
  },
  mutations: {
    set(state, { key, value }) {
      state[key] = value
    },
  },
  actions: {
    closeModal({ commit }) {
      commit('set', { key: 'isOpen', value: false })
    },

    load({ commit, rootGetters }, { legalCookies, forceOptin = false }) {
      // Update the state with the values from the session cookies
      legalCookies.forEach(({ key, value }) => {
        commit('set', { key, value })
      })

      const legalCookiesNames = legalCookies.map(({ key }) => key)

      // Find the cookies keys that are not in the session cookies
      const missingCookies = Object.keys(COOKIES).filter(
        (cookieName) => !legalCookiesNames.includes(cookieName),
      )

      const isOpen = missingCookies.length > 0

      if (COUNTRIES_WITHOUT_MODAL.includes(rootGetters['config/country'])) {
        // If the experiment value is 'withoutCookiesModal', never show the modal
        // Otherwise, the modal is displayed if a cookie is missing in session cookies
        if (
          rootGetters['flags/getExperiments'].cookiesModalDisplay !==
          'withoutCookiesModal'
        ) {
          commit('set', { key: 'isOpen', value: isOpen })
        }

        // Override the default state only when the experiment is running
        if (
          rootGetters['flags/getExperiments'].cookiesModalDisplay !==
          'noVariation'
        ) {
          missingCookies.forEach((cookieName) => {
            commit('set', { key: cookieName, value: forceOptin })
          })

          // [ACQ-1194] To remove when the experiment campaign is done
          // We want to send events to Amplitude, even if the user already rejected analytics cookies
          if (forceOptin) {
            commit('set', { key: TYPES.ANALYTICS, value: true })
          }
        }
      } else {
        commit('set', { key: 'isOpen', value: isOpen })
      }
    },

    setSessionCookies({ commit }, choices) {
      Object.entries(COOKIES).forEach(([key, cookie]) => {
        if (!isUndefined(choices[key])) {
          const value = choices[key]
          commit('set', {
            key,
            value,
          })

          setCookie(cookie, value, createConfig())
        }
      })
    },

    async save(
      { state = DEFAULT_STATE, dispatch, rootGetters },
      { forceOptin = false, ...settings } = {},
    ) {
      try {
        const identifier = rootGetters['config/visitorId']

        const choices = Object.keys(COOKIES).reduce(
          (acc, key) => ({
            ...acc,
            [key]: isUndefined(settings[key]) ? state[key] : settings[key],
          }),
          { [TYPES.FUNCTIONAL]: true },
        )

        await retry(() =>
          dispatch(
            'http/request',
            {
              request: saveCookiesSettings,
              body: {
                identifier,
                choices,
              },
            },
            {
              root: true,
            },
          ),
        )

        // [ACQ-1194] Remove this condition
        // we want to send analytics events to Amplitude
        // Only in US and when the FF_FORCE_COOKIES_OPTIN and the cookiesModalDisplay experiment are enabled
        if (
          COUNTRIES_WITHOUT_MODAL.includes(rootGetters['config/country']) &&
          (rootGetters['flags/getExperiments'].cookiesModalDisplay !==
            'withCookiesModal' ||
            rootGetters['flags/getExperiments'].cookiesModalDisplay !==
              'withoutCookiesModal') &&
          forceOptin
        ) {
          trackGDPR({ ...choices, [TYPES.ANALYTICS]: true })
        } else {
          trackGDPR(choices)
        }

        dispatch('setSessionCookies', choices)
      } catch (cause) {
        throw new Error('Retry failed', { cause })
      }
    },
  },
}
